import { useEffect, useState } from 'react'
import { useAuth } from '@clerk/clerk-react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Link } from 'react-router-dom'
import { useTranslation } from 'react-i18next'

import { Button } from '@/components/ui/button'
import { Card } from '@/components/ui/card'
import { formatDateTime } from '@/lib/dates'
import { track } from '@/lib/analytics'

type Entry = {
  id: string
  event: string
  actor_clerk_user_id: string
  actor_email: string | null
  target_shop_id: string | null
  target_shop_name: string | null
  target_clerk_org_id: string | null
  detail: Record<string, unknown> | null
  created_at: string
}

type Resp = { entries: Entry[] }

const UNDO_WINDOW_MS = 60 * 60 * 1000

export function AdminAuditLog() {
  const { t } = useTranslation('admin')
  const { t: tCommon } = useTranslation('common')
  const { getToken, isLoaded } = useAuth()

  const query = useQuery<Resp>({
    queryKey: ['admin', 'audit'],
    enabled: isLoaded,
    queryFn: async () => {
      const token = await getToken()
      if (!token) throw new Error(tCommon('error.missing_token'))
      const res = await fetch('/api/admin/audit?limit=100', {
        headers: { Authorization: `Bearer ${token}` },
      })
      if (!res.ok) {
        throw new Error(
          tCommon('error.request_failed', { status: res.status }),
        )
      }
      return (await res.json()) as Resp
    },
  })

  return (
    <div className="space-y-5">
      <div>
        <h1 className="text-2xl font-bold tracking-tight text-foreground">
          {t('audit.title')}
        </h1>
        <p className="mt-1 text-sm text-muted-foreground">
          {t('audit.subtitle')}
        </p>
      </div>

      {query.isError ? (
        <Card className="border-destructive/30 bg-destructive/10 p-4 text-sm text-destructive">
          {query.error instanceof Error
            ? query.error.message
            : t('audit.error')}
        </Card>
      ) : null}
      {query.isLoading ? (
        <Card className="p-4 text-sm text-muted-foreground">
          {tCommon('loading')}
        </Card>
      ) : null}

      {query.data && query.data.entries.length === 0 ? (
        <Card className="p-8 text-center text-sm text-muted-foreground">
          {t('audit.empty')}
        </Card>
      ) : null}

      {query.data && query.data.entries.length > 0 ? (
        <Card className="overflow-hidden p-0">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-border bg-muted/40 text-left text-xs uppercase tracking-wide text-muted-foreground">
                  <th className="px-4 py-3 font-semibold">
                    {t('audit.table.when')}
                  </th>
                  <th className="px-4 py-3 font-semibold">
                    {t('audit.table.event')}
                  </th>
                  <th className="px-4 py-3 font-semibold">
                    {t('audit.table.actor')}
                  </th>
                  <th className="px-4 py-3 font-semibold">
                    {t('audit.table.target')}
                  </th>
                  <th className="px-4 py-3 font-semibold text-right" aria-label="actions"></th>
                </tr>
              </thead>
              <tbody>
                {query.data.entries.map((e) => (
                  <tr
                    key={e.id}
                    className="border-b border-border/60 last:border-0"
                  >
                    <td className="whitespace-nowrap px-4 py-3 text-xs text-muted-foreground">
                      {formatDateTime(e.created_at)}
                    </td>
                    <td className="px-4 py-3">
                      <EventLabel event={e.event} />
                    </td>
                    <td className="px-4 py-3 text-foreground">
                      {e.actor_email ?? (
                        <span className="font-mono text-[11px] text-muted-foreground">
                          {e.actor_clerk_user_id}
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      {e.target_shop_id ? (
                        <Link
                          to={`/tenants/${e.target_shop_id}`}
                          className="text-brand hover:underline"
                        >
                          {e.target_shop_name ?? e.target_shop_id}
                        </Link>
                      ) : (
                        '—'
                      )}
                    </td>
                    <td className="whitespace-nowrap px-4 py-3 text-right">
                      <UndoRestoreCell entry={e} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      ) : null}
    </div>
  )
}

/**
 * Renders an Undo button for `tenant_data_restore` entries within the
 * 1h undo window. Hides itself once the window expires (re-checked
 * every 30s while the page is open). On success, invalidates the
 * audit log query so the row's `detail` reflects the undo and the
 * matching tenant's data-safety queries refetch.
 */
function UndoRestoreCell({ entry }: { entry: Entry }) {
  const { getToken } = useAuth()
  const { t } = useTranslation('admin')
  const { t: tCommon } = useTranslation('common')
  const queryClient = useQueryClient()

  const isRestoreEvent = entry.event === 'tenant_data_restore'
  const detail = (entry.detail ?? null) as Record<string, unknown> | null
  const restoreId = isRestoreEvent
    ? typeof detail?.restore_id === 'string'
      ? (detail.restore_id as string)
      : null
    : null
  const table =
    isRestoreEvent && typeof detail?.table_name === 'string'
      ? (detail.table_name as string)
      : null

  const createdAtMs = Date.parse(entry.created_at)
  const [now, setNow] = useState(() => Date.now())
  useEffect(() => {
    if (!isRestoreEvent || !restoreId) return
    const id = setInterval(() => setNow(Date.now()), 30_000)
    return () => clearInterval(id)
  }, [isRestoreEvent, restoreId])

  const expired = now - createdAtMs > UNDO_WINDOW_MS
  const minutesLeft = Math.max(
    0,
    Math.ceil((UNDO_WINDOW_MS - (now - createdAtMs)) / 60_000),
  )

  const [terminalError, setTerminalError] = useState<string | null>(null)

  const undoMut = useMutation({
    mutationFn: async () => {
      if (!restoreId || !entry.target_shop_id) {
        throw new Error('missing_restore_target')
      }
      const token = await getToken()
      if (!token) throw new Error(tCommon('error.missing_token'))
      const res = await fetch(
        `/api/admin/tenants/${entry.target_shop_id}/data-safety/undo-restore`,
        {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ restore_id: restoreId }),
        },
      )
      if (!res.ok) {
        const body = (await res.json().catch(() => ({}))) as { error?: string }
        // 404 = expired or unknown; 409 = already used; 410 = window expired.
        // All three are terminal — hide the button so the operator can't
        // keep retrying. The data-safety query elsewhere will catch up.
        if (res.status === 404 || res.status === 409 || res.status === 410) {
          setTerminalError(body.error ?? `undo_${res.status}`)
        }
        throw new Error(body.error ?? `undo_failed_${res.status}`)
      }
      return res.json()
    },
    onSuccess: () => {
      track('admin.data_safety.restore_undone', {
        tenant_id: entry.target_shop_id,
        table,
        interval: null,
        minutes_after_restore: Math.max(
          0,
          Math.round((Date.now() - createdAtMs) / 60_000),
        ),
      })
      queryClient.invalidateQueries({ queryKey: ['admin', 'audit'] })
      if (entry.target_shop_id) {
        queryClient.invalidateQueries({
          queryKey: ['admin', 'data-safety', entry.target_shop_id],
        })
      }
    },
  })

  if (!isRestoreEvent || !restoreId) return null
  if (expired || terminalError) return null

  return (
    <div className="flex flex-col items-end gap-1">
      <Button
        size="sm"
        variant="outline"
        disabled={undoMut.isPending}
        onClick={() => undoMut.mutate()}
      >
        {undoMut.isPending
          ? t('audit.undo.in_progress')
          : t('audit.undo.action', { minutes: minutesLeft })}
      </Button>
      {undoMut.isError && !terminalError ? (
        <div className="text-[10px] text-rose-700">
          {undoMut.error instanceof Error ? undoMut.error.message : 'undo failed'}
        </div>
      ) : null}
    </div>
  )
}

function EventLabel({ event }: { event: string }) {
  const { t } = useTranslation('admin')
  const known = [
    'tenant.invited',
    'tenant.provisioned',
    'tenant.suspended',
    'tenant.reinstated',
    'tenant.owner_reinvited',
    'tenant_data_restore',
    'tenant_data_restore_undone',
  ]
  const label = known.includes(event)
    ? t(`audit.event_label.${event}` as const)
    : t('audit.event_label.unknown', { event })
  return (
    <span className="inline-flex items-center rounded-full bg-brand-light px-2 py-0.5 text-[11px] font-semibold text-brand-dark">
      {label}
    </span>
  )
}
